/**
 * PCM helpers shared by every transport. All audio inside the engine is
 * 16-bit signed little-endian mono PCM held in an Int16Array; transports
 * convert at the edge (G.711 mu-law for Twilio, raw PCM for Exotel and the
 * browser). No Node Buffer here: this module also runs in the browser.
 */

export function bytesToPcm16(bytes: Uint8Array): Int16Array {
  const n = bytes.length >> 1;
  const out = new Int16Array(n);
  const view = new DataView(bytes.buffer, bytes.byteOffset, n * 2);
  for (let i = 0; i < n; i++) out[i] = view.getInt16(i * 2, true);
  return out;
}

export function pcm16ToBytes(samples: Int16Array): Uint8Array {
  const out = new Uint8Array(samples.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < samples.length; i++) view.setInt16(i * 2, samples[i] ?? 0, true);
  return out;
}

export function concatPcm(chunks: Int16Array[]): Int16Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Int16Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

// ---- G.711 mu-law -------------------------------------------------------

const MULAW_BIAS = 0x84;
const MULAW_CLIP = 32635;

export function mulawEncodeSample(sample: number): number {
  let s = Math.max(-32768, Math.min(32767, sample | 0));
  const sign = s < 0 ? 0x80 : 0;
  if (sign) s = -s;
  if (s > MULAW_CLIP) s = MULAW_CLIP;
  s += MULAW_BIAS;
  let exponent = 7;
  for (let mask = 0x4000; (s & mask) === 0 && exponent > 0; mask >>= 1) exponent--;
  const mantissa = (s >> (exponent + 3)) & 0x0f;
  return ~(sign | (exponent << 4) | mantissa) & 0xff;
}

export function mulawDecodeSample(byte: number): number {
  const u = ~byte & 0xff;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  let s = ((mantissa << 3) + MULAW_BIAS) << exponent;
  s -= MULAW_BIAS;
  return sign ? -s : s;
}

export function mulawEncode(samples: Int16Array): Uint8Array {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = mulawEncodeSample(samples[i] ?? 0);
  return out;
}

export function mulawDecode(bytes: Uint8Array): Int16Array {
  const out = new Int16Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = mulawDecodeSample(bytes[i] ?? 0xff);
  return out;
}

// ---- Resampling ---------------------------------------------------------

/**
 * Linear-interpolation resampler. Adequate for speech between 8, 16, 22.05
 * and 24 kHz; when downsampling it first applies a short moving-average
 * low-pass so telephony audio does not alias.
 */
export function resample(samples: Int16Array, fromRate: number, toRate: number): Int16Array {
  if (fromRate === toRate || samples.length === 0) return samples;
  let src = samples;
  if (toRate < fromRate) {
    const taps = Math.max(1, Math.round(fromRate / toRate));
    if (taps > 1) {
      const filtered = new Int16Array(samples.length);
      let acc = 0;
      for (let i = 0; i < samples.length; i++) {
        acc += samples[i] ?? 0;
        if (i >= taps) acc -= samples[i - taps] ?? 0;
        filtered[i] = Math.round(acc / Math.min(i + 1, taps));
      }
      src = filtered;
    }
  }
  const ratio = fromRate / toRate;
  const outLength = Math.floor(src.length / ratio);
  const out = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, src.length - 1);
    const frac = pos - i0;
    out[i] = Math.round((src[i0] ?? 0) * (1 - frac) + (src[i1] ?? 0) * frac);
  }
  return out;
}

// ---- WAV ------------------------------------------------------------------

export function encodeWav(samples: Int16Array, sampleRate: number): Uint8Array {
  const dataBytes = samples.length * 2;
  const out = new Uint8Array(44 + dataBytes);
  const v = new DataView(out.buffer);
  const ascii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(offset + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  v.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  v.setUint32(16, 16, true); // fmt chunk size
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true); // byte rate
  v.setUint16(32, 2, true); // block align
  v.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  v.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) v.setInt16(44 + i * 2, samples[i] ?? 0, true);
  return out;
}

export interface DecodedWav {
  sampleRate: number;
  samples: Int16Array; // mono
}

/** Decode a PCM16 (or mu-law) WAV, downmixing to mono. Skips unknown chunks. */
export function decodeWav(bytes: Uint8Array): DecodedWav {
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (offset: number) =>
    String.fromCharCode(v.getUint8(offset), v.getUint8(offset + 1), v.getUint8(offset + 2), v.getUint8(offset + 3));
  if (bytes.length < 12 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE') {
    throw new Error('not a RIFF/WAVE file');
  }
  let offset = 12;
  let format = 0;
  let channels = 1;
  let sampleRate = 0;
  let bits = 16;
  while (offset + 8 <= bytes.length) {
    const id = tag(offset);
    let size = v.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      format = v.getUint16(body, true);
      channels = v.getUint16(body + 2, true);
      sampleRate = v.getUint32(body + 4, true);
      bits = v.getUint16(body + 14, true);
    } else if (id === 'data') {
      // Streaming encoders sometimes write 0 or 0xFFFFFFFF as the data size.
      if (size === 0 || size === 0xffffffff || body + size > bytes.length) size = bytes.length - body;
      const raw = bytes.subarray(body, body + size);
      let interleaved: Int16Array;
      if (format === 7 && bits === 8) interleaved = mulawDecode(raw);
      else if (format === 1 && bits === 16) interleaved = bytesToPcm16(raw);
      else if (format === 0xfffe && bits === 16) interleaved = bytesToPcm16(raw);
      else throw new Error(`unsupported WAV encoding: format ${format}, ${bits}-bit`);
      if (channels <= 1) return { sampleRate, samples: interleaved };
      const frames = Math.floor(interleaved.length / channels);
      const mono = new Int16Array(frames);
      for (let f = 0; f < frames; f++) {
        let acc = 0;
        for (let c = 0; c < channels; c++) acc += interleaved[f * channels + c] ?? 0;
        mono[f] = Math.round(acc / channels);
      }
      return { sampleRate, samples: mono };
    }
    offset = body + size + (size & 1);
  }
  throw new Error('WAV has no data chunk');
}

// ---- Level ----------------------------------------------------------------

/** RMS level in dBFS (0 = full scale; silence is -Infinity, clamped to -100). */
export function rmsDbfs(samples: Int16Array, start = 0, end = samples.length): number {
  const n = end - start;
  if (n <= 0) return -100;
  let sum = 0;
  for (let i = start; i < end; i++) {
    const s = (samples[i] ?? 0) / 32768;
    sum += s * s;
  }
  const rms = Math.sqrt(sum / n);
  return rms > 0 ? Math.max(-100, 20 * Math.log10(rms)) : -100;
}

export function durationMs(samples: Int16Array | number, sampleRate: number): number {
  const n = typeof samples === 'number' ? samples : samples.length;
  return Math.round((n / sampleRate) * 1000);
}
